import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { EsgFeedArrows } from './EsgFeedArrows';

const rowsData = [
  {
    name: 'True Corporation Public Company Limited (SET:TRUE)',
    industry: 'Internet Software and Services	',
    geography: 'Asia-Pacific',
    esgScore: 93,
    environmentalScore: 90,
    socialScore: 98,
    economicScore: 90,
  },
  {
    name: 'ASE Technology Holding Co., Ltd. (TWSE:3711)	',
    industry: 'Semiconductors',
    geography: 'Asia-Pacific',
    esgScore: 92,
    environmentalScore: 96,
    socialScore: 92,
    economicScore: 90,
  },
  {
    name: 'PTT Global Chemical Public Company Limited (SET:PTTGC)	',
    industry: 'Commodity Chemicals	',
    geography: 'Asia-Pacific',
    esgScore: 92,
    environmentalScore: 98,
    socialScore: 92,
    economicScore: 85,
  },
  {
    name: 'United Microelectronics Corporation (TWSE:2303)	',
    industry: 'Semiconductors	',
    geography: 'Asia-Pacific',
    esgScore: 92,
    environmentalScore: 99,
    socialScore: 91,
    economicScore: 86,
  },
  {
    name: 'Deutsche Telekom AG (XTRA:DTE)',
    industry: 'Integrated Telecommunication Services	',
    geography: 'Europe',
    esgScore: 91,
    environmentalScore: 97,
    socialScore: 96,
    economicScore: 85,
  },
  {
    name: 'EDP - Energias de Portugal, S.A. (ENXTLS:EDP)	',
    industry: 'Electric Utilities		',
    geography: 'Europe',
    esgScore: 91,
    environmentalScore: 92,
    socialScore: 92,
    economicScore: 88,
  },
  {
    name: 'Taiwan Mobile Co., Ltd. (TWSE:3045)		',
    industry: 'Wireless Telecommunication Services',
    geography: 'Asia-Pacific	',
    esgScore: 91,
    environmentalScore: 97,
    socialScore: 95,
    economicScore: 86,
  },
];

function createData(
  name,
  industry,
  geography,
  esgScore,
  environmentalScore,
  socialScore,
  economicScore
) {
  return {
    name,
    industry,
    geography,
    esgScore,
    environmentalScore,
    socialScore,
    economicScore,
  };
}

const rows = rowsData.map(
  ({ name, industry, geography, esgScore, environmentalScore, socialScore, economicScore }) =>
    createData(name, industry, geography, esgScore, environmentalScore, socialScore, economicScore)
);

const headCells = [
  {
    id: 'name',
    numeric: false,
    label: 'Entity Name',
    align: 'left',
  },
  {
    id: 'industry',
    numeric: false,
    label: 'Industry Classification',
    align: 'left',
  },
  {
    id: 'geography',
    numeric: false,
    label: 'Geography',
    align: 'center',
  },
  {
    id: 'esgScore',
    numeric: true,
    label: 'ESG Score',
    align: 'center',
  },
  {
    id: 'environmentalScore',
    numeric: true,
    label: 'ESG Environmental Score',
    align: 'center',
  },
  {
    id: 'socialScore',
    numeric: true,
    label: 'ESG Social Score',
    align: 'center',
  },
  {
    id: 'economicScore',
    numeric: true,
    label: 'ESG Governance Economic Score ',
    align: 'center',
  },
];

const tableCell = {
  'td,th': {
    fontFamily: 'Montserrat',
    fontSize: '16px',
    lineHeight: '24px',
    color: '#212121',
    padding: '16px 7px',
    borderBottom: '1px solid  #EFEFEF',
    marginBottom: '8px',
  },

  boxShadow: '0px',
};

const tableRow = {
  transition: 'all .3s ease',

  '&:hover': {
    backgroundColor: 'rgba(140, 157, 189, 0.12)',
    'td, th': {
      color: '#18397A',
    },
  },
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const TableHeader = ({ order, orderBy, onRequestSort }) => {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            sortDirection={orderBy === headCell.id ? order : false}
            key={headCell.id}
            align={headCell.align}
            sx={{ fontWeight: 500 }}
          >
            {headCell.numeric ? (
              <TableSortLabel
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent={EsgFeedArrows}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              <TableSortLabel
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent=""
              >
                {headCell.label}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const EsgTable = () => {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ width: '100%', padding: '0px 20px 20px 20px' }} elevation={0}>
      <Paper sx={{ width: '100%', mb: 2 }} elevation={0}>
        <TableContainer>
          <Table sx={tableCell} aria-labelledby="tableTitle">
            <TableHeader order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy)).map((row, index) => (
                <TableRow sx={tableRow} role="checkbox" tabIndex={-1} key={row.name}>
                  <TableCell component="th" scope="row" align="left">
                    {row.name}
                  </TableCell>
                  <TableCell align="left">{row.industry}</TableCell>
                  <TableCell align="left">{row.geography}</TableCell>
                  <TableCell align="center">{row.esgScore}</TableCell>
                  <TableCell align="center">{row.environmentalScore}</TableCell>
                  <TableCell align="center">{row.socialScore}</TableCell>
                  <TableCell align="center">{row.economicScore}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EsgTable;
